import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useRecommendations } from '../api';
import { ProductGrid } from '../components/product/ProductGrid';
import { formatPrice } from '../utils/format';
import { UI_CONSTANTS, CART_CONSTANTS } from '@petmas/shared';

export function CartPage() {
  const { items, subtotal, removeItem, updateQuantity, clearCart } = useCart();
  const { data: recommendations } = useRecommendations();

  if (items.length === 0) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container py-12 text-center">
          <CartEmptyIcon className="w-20 h-20 mx-auto text-gray-300" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">
            Tu carrito esta vacio
          </h1>
          <p className="mt-2 text-gray-600">
            Agrega productos para comenzar tu compra
          </p>
          <Link to="/" className="mt-6 btn btn-primary inline-block">
            Explorar productos
          </Link>
        </div>

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <section className="py-12">
            <div className="container">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Te puede interesar
              </h2>
              <ProductGrid products={recommendations.slice(0, UI_CONSTANTS.RELATED_PRODUCTS_LIMIT)} />
            </div>
          </section>
        )}
      </div>
    );
  }

  // Calculate shipping threshold
  const freeShippingThreshold = 150;
  const amountToFreeShipping = Math.max(0, freeShippingThreshold - subtotal);
  const hasFreeShipping = amountToFreeShipping === 0;

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Carrito de Compras
          </h1>
          <p className="mt-1 text-gray-600">
            {items.length} {items.length === 1 ? 'producto' : 'productos'}
          </p>
        </div>
      </div>

      {/* Free shipping progress */}
      {!hasFreeShipping && (
        <div className="bg-primary/5 border-b border-primary/10">
          <div className="container py-3">
            <p className="text-sm text-secondary">
              <span className="font-medium">Envio Gratis:</span> Te faltan{' '}
              <span className="font-bold">{formatPrice(amountToFreeShipping)}</span>{' '}
              para envio gratis
            </p>
            <div className="mt-2 h-2 bg-primary/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${Math.min(CART_CONSTANTS.PROGRESS_PERCENTAGE_FULL, (subtotal / freeShippingThreshold) * CART_CONSTANTS.PROGRESS_PERCENTAGE_FULL)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Cart items */}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border">
              {/* Clear all button */}
              <div className="flex items-center justify-between p-4 border-b">
                <span className="font-medium text-gray-900">Productos</span>
                <button
                  onClick={clearCart}
                  className="text-sm text-gray-500 hover:text-red-500"
                >
                  Vaciar carrito
                </button>
              </div>

              {/* Items */}
              <ul className="divide-y">
                {items.map((item) => (
                  <li key={item.variantId} className="p-4">
                    <div className="flex gap-4">
                      {/* Image */}
                      <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.productName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <ImagePlaceholder className="w-8 h-8" />
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <Link
                              to={`/producto/${item.productId}`}
                              className="font-medium text-gray-900 hover:text-primary"
                            >
                              {item.productName}
                            </Link>
                            {item.variantName && (
                              <p className="text-sm text-gray-500 mt-0.5">
                                {item.variantName}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => removeItem(item.variantId)}
                            className="text-gray-400 hover:text-red-500 p-1"
                            aria-label="Eliminar producto"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                          {/* Quantity */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                updateQuantity(item.variantId, item.quantity - 1)
                              }
                              className="w-8 h-8 flex items-center justify-center border rounded hover:bg-gray-50"
                            >
                              -
                            </button>
                            <span className="w-10 text-center">{item.quantity}</span>
                            <button
                              onClick={() =>
                                updateQuantity(item.variantId, item.quantity + 1)
                              }
                              className="w-8 h-8 flex items-center justify-center border rounded hover:bg-gray-50"
                            >
                              +
                            </button>
                          </div>

                          {/* Price */}
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              {formatPrice(item.price * item.quantity)}
                            </p>
                            {item.quantity > 1 && (
                              <p className="text-xs text-gray-500">
                                {formatPrice(item.price)} c/u
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Order summary */}
          <aside className="lg:w-96">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Resumen del Pedido
              </h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Envio</span>
                  <span className={hasFreeShipping ? 'text-green-600 font-medium' : ''}>
                    {hasFreeShipping ? 'GRATIS' : 'Calculado en checkout'}
                  </span>
                </div>
              </div>

              <div className="border-t my-4" />

              <div className="flex justify-between text-lg font-semibold">
                <span>Total</span>
                <span>{formatPrice(subtotal)}</span>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Impuestos incluidos. Envio calculado en el checkout.
              </p>

              <Link
                to="/checkout"
                className="btn btn-primary w-full mt-6 py-3"
              >
                Proceder al Pago
              </Link>

              <Link
                to="/"
                className="btn btn-secondary w-full mt-3"
              >
                Continuar Comprando
              </Link>

              {/* Accepted payments */}
              <div className="mt-6 pt-4 border-t">
                <p className="text-xs text-gray-500 mb-2">Aceptamos:</p>
                <div className="flex flex-wrap gap-2">
                  {['Visa', 'Mastercard', 'Yape', 'Plin', 'BCP'].map((method) => (
                    <span
                      key={method}
                      className="text-xs bg-gray-100 px-2 py-1 rounded"
                    >
                      {method}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Complementa tu compra
            </h2>
            <ProductGrid products={recommendations.slice(0, UI_CONSTANTS.RELATED_PRODUCTS_LIMIT)} />
          </div>
        </section>
      )}
    </div>
  );
}

function CartEmptyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function ImagePlaceholder({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}
